import { Button, toast } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { useContractsQuery } from "../api/queries";
import { DocumentPortalTour } from "../tour/DocumentPortalTour";
import { contractsTourSteps } from "../tour/documentPortalTourSteps";
import { useDocumentPortalTour } from "../tour/useDocumentPortalTour";
import { ContractDocumentCard } from "../ui/ContractDocumentCard";

export function MyContractsPage() {
  const navigate = useNavigate();
  const tour = useDocumentPortalTour("doc-contracts-tour-completed");
  const query = useContractsQuery();
  const data = query.data?.data ?? [];

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex justify-between space-y-4x">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ສັນຍາຂອງຂ້ອຍ</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-0.5 font-medium text-muted-foreground">
              ເບິ່ງສັນຍາຈ້າງງານຂອງທ່ານ
            </p>
            <div className="mt-2 h-1 w-16 rounded-full bg-black" />
          </div>
          <div>
            <Button
              variant="default"
              className="w-full border-black font-medium hover:bg-black hover:text-white"
              onClick={() => navigate({ to: "/app/dashboard" })}
            >
              <ArrowLeft className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <div
          data-tourid="doc-contracts-grid"
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {query.isLoading ? (
            Array.from({ length: 6 }, (_, i) => `skeleton-contract-${i}`).map(
              (key) => (
                <div
                  key={key}
                  className="h-52 animate-pulse rounded-xl bg-muted"
                />
              ),
            )
          ) : data.length === 0 ? (
            <div className="col-span-full rounded-xl border-2 border-border border-dashed bg-muted/20 p-12 text-center">
              <p className="text-muted-foreground">ບໍ່ມີສັນຍາທີ່ຜູກກັບບັນຊີຂອງທ່ານ</p>
            </div>
          ) : (
            data.map((item) => (
              <ContractDocumentCard
                key={item.id}
                item={item}
                onView={(contractId) =>
                  navigate({
                    to: "/app/documents/contracts/$id",
                    params: { id: contractId },
                  })
                }
                onDownload={(contractId) => {
                  const contract = data.find((d) => d.id === contractId);
                  const downloadUrl = resolveImageSrc(contract?.fileKey);
                  if (!downloadUrl) {
                    toast.error("ບໍ່ພົບເສັ້ນທາງໄຟລ໌ສຳລັບດາວໂຫລດ");
                    return;
                  }
                  const link = document.createElement("a");
                  link.href = downloadUrl;
                  link.target = "_blank";
                  link.rel = "noopener noreferrer";
                  link.download = contract?.title || "contract";
                  link.click();
                }}
              />
            ))
          )}
        </div>
        <DocumentPortalTour
          steps={contractsTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
